import { useMemo } from "react";
import { useFormik } from "formik";
import {
  CreateDynamicSchema,
  RequiredField,
  ConditionalField,
  ConditionalFieldNot,
  OPTION_MSG,
  REQUIRED_MSG,
} from "../schema/formik";
import { IMisdeliveryInvestigation } from "../models";

export const misdeliveryInvestigationInitialValues: IMisdeliveryInvestigation =
  {
    osdType: undefined,
    osdNumber: undefined,
    username: undefined,
    // Pickup Recovery Section
    wasPickUpCompleted: undefined,
    wasPickUpRescheduled: undefined,
    pickupNumber: undefined,
    reasonUnableToPickUpCode: undefined,
    otherReasonComments: undefined,
    // Freight Location Verification Section
    wasNewProAttachedToBol: undefined,
    isFreightInOsdBay: undefined,
    didFreightMoveOnOd400: undefined,
    wasOd400CreatedByDock: undefined,
    od400ProNumber: undefined,
    willOriginalProBecomeShort: undefined,
    misdeliveryPickupProNumber: undefined,
    asExceptionNumber: undefined,
    // Address & Billing Verification Section
    doesAs400AddressMatchBol: undefined,
    doesBolShowSlc: undefined,
    isFreightLabeled: undefined,
    isFreightLabeledShowingMsdAddress: undefined,
    // Freight Label Address Fields
    misdeliveryAddress1: undefined,
    misdeliveryAddress2: undefined,
    misdeliveryCity: undefined,
    misdeliveryState: undefined,
    misdeliveryZip: undefined,
    // MISDELV Shipment Tracking Identification Section
    doesMsdShipmentHaveLabels: undefined,
    isCrossShipment: undefined,
    doesFreightHaveSaiaProSticker: undefined,
    isProInTransit: undefined,
    deliveryStatusOnSaiaPro: undefined,
    suffixBillForAppliesToPro: undefined,
    appliedToProShipmentMovingOn: undefined,
    ovExceptionNumber: undefined,
    nbException: undefined,
    // Freight Handling Section
    driverName: undefined,
    dockWorkerName: undefined,
    // Optional fields (BOL info)
    newProNumber: undefined,
  };

const validationConfig = [
  // ─── Pickup Recovery Section ─────────────────────────────────────────────────
  // wasPickUpCompleted: Always required (Y/N)
  RequiredField("wasPickUpCompleted", OPTION_MSG),

  // wasPickUpRescheduled: Required when wasPickUpCompleted === "N"
  ConditionalField(
    "wasPickUpRescheduled",
    "wasPickUpCompleted",
    "N",
    true,
    OPTION_MSG,
  ),

  // pickupNumber: Required when wasPickUpRescheduled === "Y" (number)
  ConditionalField(
    "pickupNumber",
    "wasPickUpRescheduled",
    "Y",
    true,
    REQUIRED_MSG,
    "number",
  ),

  // reasonUnableToPickUpCode: Required when wasPickUpRescheduled === "N"
  ConditionalField(
    "reasonUnableToPickUpCode",
    "wasPickUpRescheduled",
    "N",
    true,
    REQUIRED_MSG,),
    ConditionalField(
    "wasNewProAttachedToBol",
    "wasPickUpCompleted",
    "Y",
    true,
    OPTION_MSG,
  ),
  ConditionalField(
    "newProNumber",
    "wasNewProAttachedToBol",
    "N",
    true,
    REQUIRED_MSG,
    "string",
    false,
    11,
    "New PRO Number must be at most 11 digits",
    false,
    undefined,
    undefined,
    11,
    "New PRO Number must be at least 11 digits",
  ),

  // otherReasonComments: Required when reasonUnableToPickUpCode === "OR"
  ConditionalField(
    "otherReasonComments",
    "reasonUnableToPickUpCode",
    "OR",
    true,
    REQUIRED_MSG,
    "string",
    false,
    200,
    "Additional comments must be at most 200 characters",
  ),

  // ─── Freight Location Verification Section ───────────────────────────────────
  // isFreightInOsdBay: Always required (Y/N)
  RequiredField("isFreightInOsdBay", OPTION_MSG),

  // didFreightMoveOnOd400: Required when isFreightInOsdBay === "N"
  ConditionalField(
    "didFreightMoveOnOd400",
    "isFreightInOsdBay",
    "N",
    true,
    OPTION_MSG,
  ),

  // wasOd400CreatedByDock: Required when didFreightMoveOnOd400 === "Y"
  ConditionalField(
    "wasOd400CreatedByDock",
    "didFreightMoveOnOd400",
    "Y",
    true,
    OPTION_MSG,
  ),

  // od400ProNumber: Required when wasOd400CreatedByDock === "Y" (number, max 11 digits, min 11 digits)
  ConditionalField(
    "od400ProNumber",
    "wasOd400CreatedByDock",
    "Y",
    true,
    REQUIRED_MSG,
    "number",
    false,
    11,
    "Must be at most 11 digits",
    false,
    undefined,
    undefined,
    11,
    "Must be at least 11 digits",
  ),

  // misdeliveryPickupProNumber: Required when didFreightMoveOnOd400 === "N" (number, max 11 digits, min 11 digits)
  ConditionalField(
    "misdeliveryPickupProNumber",
    "didFreightMoveOnOd400",
    "N",
    true,
    REQUIRED_MSG,
    "number",
    false,
    11,
    "Must be at most 11 digits",
    false,
    undefined,
    undefined,
    11,
    "Must be at least 11 digits",
  ),

  // asExceptionNumber: Required when didFreightMoveOnOd400 === "N" (number, max 9 digits)
  ConditionalField(
    "asExceptionNumber",
    "didFreightMoveOnOd400",
    "N",
    true,
    REQUIRED_MSG,
    "number",
    false,
    9,
    "Must be at most 9 digits",
  ),

  // ─── Section shown when didFreightMoveOnOd400 !== "N" ────────────────────────
  // willOriginalProBecomeShort: Required when didFreightMoveOnOd400 !== "N"
  ConditionalFieldNot(
    "willOriginalProBecomeShort",
    "didFreightMoveOnOd400",
    "N",
    true,
    OPTION_MSG,
  ),

  // ─── Address & Billing Verification Section ──────────────────────────────────
  // doesAs400AddressMatchBol: Required when didFreightMoveOnOd400 !== "N"
  ConditionalFieldNot(
    "doesAs400AddressMatchBol",
    "didFreightMoveOnOd400",
    "N",
    true,
    OPTION_MSG,
  ),

  // doesBolShowSlc: Required when doesAs400AddressMatchBol !== "N"
  ConditionalFieldNot(
    "doesBolShowSlc",
    "doesAs400AddressMatchBol",
    "N",
    true,
    OPTION_MSG,
  ),

  // isFreightLabeled: Required when doesAs400AddressMatchBol !== "N"
  ConditionalFieldNot(
    "isFreightLabeled",
    "doesAs400AddressMatchBol",
    "N",
    true,
    OPTION_MSG,
  ),

  // isFreightLabeledShowingMsdAddress: Required when isFreightLabeled === "Y"
  ConditionalField(
    "isFreightLabeledShowingMsdAddress",
    "isFreightLabeled",
    "Y",
    true,
    OPTION_MSG,
  ),

  // ─── Freight Label Address Fields ────────────────────────────────────────────
  // These fields are required when isFreightLabeledShowingMsdAddress === "N"
  ConditionalField(
    "misdeliveryAddress1",
    "isFreightLabeledShowingMsdAddress",
    "N",
    true,
    REQUIRED_MSG,
    "string",
    false,
    30,
    "Address 1 must be at most 30 characters",
  ),
  {
    name: "misdeliveryAddress2",
    type: "string",
    max: 30,
    maxMessage: "Address 2 must be at most 30 characters",
  },
  ConditionalField(
    "misdeliveryCity",
    "isFreightLabeledShowingMsdAddress",
    "N",
    true,
    REQUIRED_MSG,
  ),
  ConditionalField(
    "misdeliveryState",
    "isFreightLabeledShowingMsdAddress",
    "N",
    true,
    REQUIRED_MSG,
  ),
  ConditionalField(
    "misdeliveryZip",
    "isFreightLabeledShowingMsdAddress",
    "N",
    true,
    REQUIRED_MSG,
  ),

  // ─── MISDELV Shipment Tracking Identification Section ────────────────────────
  // doesMsdShipmentHaveLabels: Required when doesAs400AddressMatchBol !== "N"
  ConditionalFieldNot(
    "doesMsdShipmentHaveLabels",
    "doesAs400AddressMatchBol",
    "N",
    true,
    OPTION_MSG,
  ),

  // isCrossShipment: Required when doesMsdShipmentHaveLabels === "Y"
  ConditionalField(
    "isCrossShipment",
    "doesMsdShipmentHaveLabels",
    "Y",
    true,
    OPTION_MSG,
  ),

  // doesFreightHaveSaiaProSticker: Required when doesMsdShipmentHaveLabels === "Y"
  ConditionalField(
    "doesFreightHaveSaiaProSticker",
    "doesMsdShipmentHaveLabels",
    "Y",
    true,
    OPTION_MSG,
  ),

  // isProInTransit: Required when doesFreightHaveSaiaProSticker === "N"
  ConditionalField(
    "isProInTransit",
    "doesFreightHaveSaiaProSticker",
    "N",
    true,
    OPTION_MSG,
  ),

  // deliveryStatusOnSaiaPro: Required when doesFreightHaveSaiaProSticker === "Y"
  ConditionalField(
    "deliveryStatusOnSaiaPro",
    "doesFreightHaveSaiaProSticker",
    "Y",
    true,
    OPTION_MSG,
  ),

  // suffixBillForAppliesToPro: Required when deliveryStatusOnSaiaPro === "DS" (string, max 20 chars)
  ConditionalField(
    "suffixBillForAppliesToPro",
    "deliveryStatusOnSaiaPro",
    "DS",
    true,
    REQUIRED_MSG,
    "string",
    false,
    20,
    "Must be at most 20 characters",
  ),

  // appliedToProShipmentMovingOn: Required when deliveryStatusOnSaiaPro === "AS" (number, max 11 digits, min 11 digits)
  ConditionalField(
    "appliedToProShipmentMovingOn",
    "deliveryStatusOnSaiaPro",
    "AS",
    true,
    REQUIRED_MSG,
    "number",
    false,
    11,
    "Must be at most 11 digits",
    false,
    undefined,
    undefined,
    11,
    "Must be at least 11 digits",
  ),

  // ovExceptionNumber: Required when deliveryStatusOnSaiaPro === "DC" (number, max 9 digits)
  ConditionalField(
    "ovExceptionNumber",
    "deliveryStatusOnSaiaPro",
    "DC",
    true,
    REQUIRED_MSG,
    "number",
    false,
    9,
    "Must be at most 9 digits",
  ),

  // nbException: Required when doesMsdShipmentHaveLabels === "N" (number, max 9 digits)
  ConditionalField(
    "nbException",
    "doesMsdShipmentHaveLabels",
    "N",
    true,
    REQUIRED_MSG,
    "number",
    false,
    9,
    "Must be at most 9 digits",
  ),

  // ─── Freight Handling Section ────────────────────────────────────────────────
  // driverName: Optional field (not marked required in UI), max 30 chars
  {
    name: "driverName",
    type: "string",
    max: 30,
    maxMessage: "Driver Name must be at most 30 characters",
  },

  // dockWorkerName: Optional field (not marked required in UI), max 30 chars
  {
    name: "dockWorkerName",
    type: "string",
    max: 30,
    maxMessage: "Dockworker Name must be at most 30 characters",
  },

];

export const misdeliveryInvestigationValidationSchema =
  CreateDynamicSchema(validationConfig);

const ZERO_AS_EMPTY_NUMBER_FIELDS: (keyof IMisdeliveryInvestigation)[] = [
  "pickupNumber",
  "od400ProNumber",
  "misdeliveryPickupProNumber",
  "asExceptionNumber",
  "newProNumber",
  "appliedToProShipmentMovingOn",
  "ovExceptionNumber",
  "nbException",
];

export const buildMisdeliveryInvestigationInitialValues = (
  initialData?: Partial<IMisdeliveryInvestigation>,
) => {
  const allowedKeys = Object.keys(
    misdeliveryInvestigationInitialValues,
  ) as (keyof IMisdeliveryInvestigation)[];

  const filteredInitialData: Partial<IMisdeliveryInvestigation> = {};
  for (const key of allowedKeys) {
    const value = initialData?.[key];
    if (
      value !== undefined &&
      !(
        ZERO_AS_EMPTY_NUMBER_FIELDS.includes(key) &&
        (value === 0 || value === "0")
      )
    ) {
      Object.assign(filteredInitialData, { [key]: value });
    }
  }

  return {
    ...misdeliveryInvestigationInitialValues,
    ...filteredInitialData,
  };
};

export const buildMisdeliveryInvestigationFormikConfig = (
  initialValues: IMisdeliveryInvestigation,
) => ({
  initialValues,
  enableReinitialize: true,
  validationSchema: misdeliveryInvestigationValidationSchema,
  validateOnBlur: true,
  validateOnChange: true,
  onSubmit: (_v: IMisdeliveryInvestigation) => {
    console.info("Form submitted");
  },
});

export const useMisdeliveryInvestigationForm = (
  initialData?: Partial<IMisdeliveryInvestigation>,
) => {
  const initialValues = useMemo(
    () => buildMisdeliveryInvestigationInitialValues(initialData),
    [initialData],
  );

  const formik = useFormik<IMisdeliveryInvestigation>(
    buildMisdeliveryInvestigationFormikConfig(initialValues),
  );

  return { formik };
};
